// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company list component module.
 * Provides a clean, toggle-based interface for viewing and managing companies.
 * Matches the pattern used in the Tickets page.
 *
 * Features:
 * - Toggle buttons for view switching (all, customers, suppliers)
 * - Search functionality
 * - Archive filter toggle
 * - Company creation dialog
 * - Company table with sorting
 */
import { ActionMenuItem } from '@/components/action-menu-item';
import { EducationalTooltip } from '@/components/educational-tooltip';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SyncStatusFilterMenu } from '@/components/sync-status-filter-menu';
import { TableControlContainer } from '@/components/table-control-container';
import { CrmSyncStatus } from '@/constants/crm-sync-status';
import {
  AddRounded,
  ArrowDropDownRounded,
  InfoOutlined,
  MultipleStopOutlined,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import {
  CUSTOMER_PAGE_SIZE,
  CustomerFilter,
} from '@/customer/api/get-customers';
import { CompanyDataImportDialog } from '@/customer/components/company-data-import-dialog';
import { CreateCustomerDialog } from '@/customer/components/create-customer-dialog';
import { CustomerTable } from '@/customer/components/customer-table';
import { CustomerTableSkeleton } from '@/customer/components/customer-table-skeleton';
import {
  CustomerSortProperty,
  customerSortPropertySchema,
} from '@/customer/hooks/use-customers';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLocalStorage } from '@/hooks/use-local-storage';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { debounce, omit } from 'lodash-es';
import { ChangeEventHandler, Suspense, useMemo, useRef } from 'react';
import { z } from 'zod';

/**
 * Local storage key for persisting sort preferences.
 * Maintains user's preferred sorting across sessions.
 */
const LOCAL_STORAGE_SORT_KEY = 'customer-sort';

/**
 * Default sort property when no preference is set.
 * Companies are sorted by name initially.
 */
const DEFAULT_SORT_PROPERTY: CustomerSortProperty = 'name';

/**
 * Default sort direction when no preference is set.
 * Companies are sorted in ascending order initially.
 */
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Tooltip text explaining the archived filter.
 * Helps users understand what archived companies are.
 */
const SHOW_ARCHIVED_TOOLTIP_TITLE =
  'Show all company records, including archived companies. Archived companies do not appear by default in the company list.';

/**
 * Schema for validating sort preferences.
 * Used for both URL parameters and local storage.
 */
const sortSchema = z.object({
  property: customerSortPropertySchema,
  direction: sortDirectionSchema,
});

/**
 * View type for filtering companies by role.
 * - 'all': Show all companies
 * - 'customers': Show only customer-role companies
 * - 'suppliers': Show only supplier-role companies
 */
type ViewType = 'all' | 'customers' | 'suppliers';

/**
 * Options for the company view filter toggle.
 */
const COMPANY_VIEW_OPTIONS: FilterOption<ViewType>[] = [
  { value: 'all', label: 'All companies' },
  { value: 'customers', label: 'Customers' },
  { value: 'suppliers', label: 'Suppliers' },
];

/**
 * Props for the CompanyList component
 */
interface Props {
  /**
   * Current view filter (all, customers, suppliers).
   * Determines which toggle button is active and what filter is applied.
   */
  currentView: ViewType;

  /**
   * Filter configuration for the company table.
   * Includes role-based filters (isCustomer, isSupplier).
   */
  filter: CustomerFilter;

  /**
   * URL search parameters.
   * Used for maintaining state in URL.
   */
  search: {
    query?: string;
    archived?: boolean;
    syncStatuses?: CrmSyncStatus[];
    direction?: SortDirection;
    sort?: CustomerSortProperty;
    page?: number;
    new?: string;
  };
}

/**
 * Company list component with clean action bar matching Tickets pattern.
 * Displays toggle buttons for view switching and a table of companies.
 *
 * Features:
 * - Clean action bar with toggle buttons on left, action button on right
 * - Toggle buttons with sentence case labels
 * - Search field and archive toggle below action bar
 * - Company table with sorting
 * - Company creation dialog
 *
 * @example
 * ```tsx
 * <CompanyList
 *   currentView="customers"
 *   filter={{ isCustomer: true }}
 *   search={{ query: '', archived: false }}
 * />
 * ```
 */
export function CompanyList(props: Props) {
  const navigate = useNavigate();

  /**
   * Dialog state management.
   * Controls the create company dialog visibility.
   * Initialized based on URL parameters to support deep linking.
   */
  const dialog = useDisclosure(Boolean(props.search.new));
  const uploadDialog = useDisclosure();
  const actionMenu = useDisclosure();
  const actionGroupRef = useRef<HTMLDivElement>(null);
  const menu = useDisclosure();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Sort preference persistence in local storage.
   * Maintains user's preferred sorting between sessions.
   * Falls back to default sorting if no preference exists.
   */
  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  /**
   * Debounced search query handler.
   * Updates URL parameters after user stops typing.
   * 300ms delay prevents excessive URL updates.
   */
  const updateQuery = useMemo(() => {
    return debounce(
      (query: string) =>
        navigate({
          search: { ...omit(props.search, 'page'), query } as never,
        }),
      300
    );
  }, [navigate, props.search]);

  /**
   * Current sort configuration.
   * Prioritizes URL parameters over local storage preferences.
   * Falls back to defaults if neither exists.
   *
   * Order of precedence:
   * 1. URL search parameters
   * 2. Local storage preferences
   * 3. Default values
   */
  const sort = {
    property:
      props.search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      props.search.direction ??
      sortPreference?.direction ??
      DEFAULT_SORT_DIRECTION,
  };

  /**
   * Derived state for UI controls.
   * - isArchivedChecked: Current state of archive toggle
   * - defaultCompanyName: Pre-filled name for new company
   * - defaultRoleFlags: Default role flags based on current view
   */
  const isArchivedChecked = props.search.archived ?? false;
  const defaultCompanyName = props.search.new
    ? decodeURIComponent(props.search.new)
    : undefined;
  const isBadgeInvisible = props.search.archived === undefined;

  /**
   * Determines default role flags based on current view.
   * - customers: Only customer role selected
   * - suppliers: Only supplier role selected
   * - all: Both customer and supplier roles selected
   */
  const defaultRoleFlags = {
    isCustomer: props.currentView === 'suppliers' ? false : true,
    isSupplier: props.currentView === 'customers' ? false : true,
  };

  /**
   * Updates sort configuration.
   * Saves preference to local storage and updates URL.
   *
   * @param property - Column to sort by
   * @param direction - Sort direction (ascending/descending)
   */
  const setSort = (
    property: CustomerSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({
      search: {
        ...omit(props.search, 'page'),
        direction,
        sort: property,
      } as never,
    });
  };

  /**
   * Handles search input changes.
   * Debounces updates to prevent excessive URL changes.
   */
  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  /**
   * Handles archive filter toggle.
   * Updates URL to show/hide archived companies.
   *
   * @param isArchived - Whether to show archived companies
   */
  const handleChangeArchivedFilter = (isArchived: boolean) => {
    if (isArchived) {
      return navigate({
        search: { ...omit(props.search, 'page'), archived: true } as never,
      });
    }
    navigate({
      search: { ...omit(props.search, 'page'), archived: undefined } as never,
    });
  };

  /**
   * Handles changes to the CRM sync status filter.
   * Clears the parameter when no statuses are selected.
   */
  const handleChangeSyncStatuses = (syncStatuses: CrmSyncStatus[]) => {
    navigate({
      search: {
        ...omit(props.search, 'page'),
        syncStatuses: syncStatuses.length === 0 ? undefined : syncStatuses,
      } as never,
    });
  };

  /**
   * Handles create dialog close.
   * Closes dialog and removes 'new' parameter from URL.
   */
  const handleCloseDialog = () => {
    dialog.close();
    navigate({ search: omit(props.search, 'new') as never });
  };

  /**
   * Handles column header click for sorting.
   * Toggles direction if same column, otherwise sorts ascending.
   *
   * @param property - Column to sort by
   */
  const handleSort = (property: CustomerSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }
    setSort(property, 'asc');
  };

  /**
   * Handles toggle button navigation.
   * Navigates to the appropriate route based on the selected view.
   * Uses flat route structure with first-layer index routes:
   * - 'all' navigates to /companies
   * - 'customers' navigates to /companies/customers
   * - 'suppliers' navigates to /companies/suppliers
   *
   * @param view - The selected view (all, customers, suppliers)
   */
  const handleViewChange = (view: ViewType) => {
    const routes = {
      all: '/companies',
      customers: '/companies/customers',
      suppliers: '/companies/suppliers',
    };

    navigate({
      to: routes[view],
      search: omit(props.search, 'page') as never,
    });
  };

  const handlePageChange = (page: number) => {
    navigate({ search: { ...props.search, page } as never });
  };

  const filter: CustomerFilter = {
    ...props.filter,
    page: props.search.page ?? 1,
    pageSize: CUSTOMER_PAGE_SIZE,
  };

  return (
    <Stack>
      {/* Create company dialog */}
      <CompanyDataImportDialog
        isOpen={uploadDialog.isOpen}
        onClose={uploadDialog.close}
      />
      <CreateCustomerDialog
        defaultName={defaultCompanyName}
        defaultIsCustomer={defaultRoleFlags.isCustomer}
        defaultIsSupplier={defaultRoleFlags.isSupplier}
        isOpen={dialog.isOpen}
        onClose={handleCloseDialog}
      />

      {/* Action bar - matches Tickets pattern */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterToggleGroup
            value={props.currentView}
            options={COMPANY_VIEW_OPTIONS}
            onChange={handleViewChange}
            ariaLabel="Company filter"
          />
          <IconButton
            ref={infoButtonRef}
            size="small"
            onClick={infoTooltip.open}
            aria-label="Learn about customers and suppliers"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Stack>
        <ButtonGroup
          size="small"
          variant="contained"
          ref={actionGroupRef}
          sx={{ alignSelf: 'end' }}
        >
          <Button
            startIcon={<AddRounded fontSize="small" />}
            onClick={dialog.open}
          >
            Add company
          </Button>
          <Button onClick={actionMenu.open}>
            <ArrowDropDownRounded fontSize="small" />
          </Button>
        </ButtonGroup>
        <Menu
          open={actionMenu.isOpen}
          onClose={actionMenu.close}
          onClick={actionMenu.close}
          anchorEl={actionGroupRef.current}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          MenuListProps={{ disablePadding: true }}
          slotProps={{
            paper: {
              sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
            },
          }}
        >
          <ActionMenuItem
            icon={<MultipleStopOutlined color="action" fontSize="small" />}
            label="Import company data"
            description="Import company data in bulk from a CSV file"
            onClick={uploadDialog.open}
          />
        </Menu>
      </Stack>

      {/* Educational tooltip explaining customers vs suppliers */}
      <EducationalTooltip
        title="Customers and suppliers"
        isOpen={infoTooltip.isOpen}
        onClose={infoTooltip.close}
        anchorElement={infoButtonRef.current}
        placement="bottom-start"
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Customers
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Companies you sell products or services to.</strong> They
              receive assets, shipments, and project deliverables. Use the
              customers view to track projects, fulfillment orders, and manage
              customer locations and deployed assets.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Suppliers
            </Typography>
            <Typography variant="body2" sx={{ mb: 0 }}>
              <strong>Companies you purchase from.</strong> They provide parts,
              products, or services through purchase orders and inbound
              shipments. Use the suppliers view to track purchase orders,
              inbound shipments, and manage supplier relationships.
            </Typography>
          </Box>
          <Typography variant="body2">
            <Link
              href="https://guide.hardfin.com/set-up-the-basics/companies"
              target="_blank"
            >
              Read more about companies
            </Link>
          </Typography>
        </Stack>
      </EducationalTooltip>

      {/* Table controls - search and archive toggle */}
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" spacing={1}>
          <SyncStatusFilterMenu
            value={props.search.syncStatuses ?? []}
            onChange={handleChangeSyncStatuses}
          />
        </Stack>
        <TextField
          size="small"
          label="Search companies"
          defaultValue={props.search.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
        />
        <IconButton ref={menuButtonRef} onClick={menu.open}>
          <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
            <SettingsRounded fontSize="small" />
          </Badge>
        </IconButton>
        <Popover
          anchorEl={menuButtonRef.current}
          open={menu.isOpen}
          onClose={menu.close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack paddingY={0.5}>
            <Tooltip arrow placement="left" title={SHOW_ARCHIVED_TOOLTIP_TITLE}>
              <FormControlLabel
                sx={{ paddingLeft: 2, paddingY: 0.5 }}
                label={
                  <Typography variant="body2">
                    Include archived companies
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={isArchivedChecked}
                    onChange={(_, isChecked) =>
                      handleChangeArchivedFilter(isChecked)
                    }
                  />
                }
              />
            </Tooltip>
          </Stack>
        </Popover>
      </TableControlContainer>

      {/* Company table */}
      <Suspense fallback={<CustomerTableSkeleton />}>
        <CustomerTable
          sort={sort}
          filter={filter}
          onSort={handleSort}
          onPageChange={handlePageChange}
        />
      </Suspense>
    </Stack>
  );
}
